'use client';

import { formatShortDate } from '@/utils/dateFormat';
import type { SanityDocument } from 'next-sanity';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTransition } from 'react';

interface BlogPostProps {
  post: SanityDocument;
}

export const BlogPost = ({ post }: BlogPostProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();

  const handleTagClick = (e: React.MouseEvent, tagSlug: string) => {
    e.preventDefault();
    startTransition(() => {
      const params = new URLSearchParams(searchParams?.toString());
      params.set('tag', tagSlug);
      router.push(`/blog?${params.toString()}`);
    });
  };

  return (
    <Link
      href={`/blog/${post.slug.current}`}
      className="group block h-[256px] sm:h-[280px] w-full bg-[#F7F7F7] transition-all rounded-2xl p-6 hover:scale-[1.01]"
    >
      <div className="flex flex-col justify-between h-full">
        <div className="space-y-2">
          {post.tag && (
            <span
              onClick={(e) => handleTagClick(e, post.tag.slug)}
              className={`inline-block px-2.5 py-[9px] rounded-lg text-sm leading-[14px] tracking-[-0.14px] font-medium transition-colors ${
                isPending
                  ? 'bg-[#F2F2F2] text-[#171717]'
                  : 'bg-background text-[#9A9A9A] hover:bg-[#F2F2F2]/70'
              } cursor-pointer`}
            >
              {post.tag.name}
            </span>
          )}
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-semibold tracking-[-0.48px] line-clamp-2">
            {post.title}
          </h3>
          <div className="flex items-center gap-4 text-base">
            {post.publishedAt && (
              <p className="text-[#808080] text-base">
                {formatShortDate(post.publishedAt)}
              </p>
            )}
            {post.authorObject && (
              <span
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = `/authors/${post.authorObject.slug}`;
                }}
                className="hover:text-primary/50 transition-colors text-[#9A9A9A] hover:underline cursor-pointer"
              >
                {post.authorObject.name}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};
